import { find, matchesProperty } from 'lodash'

// Preserve the locally-tracked unread message state when a room is refreshed
// from the server, and keep any in-flight dummy (skeleton) rooms.
export const updateRoomsWithUnreadMessages = (rooms, fetchedRooms) => {
  const newRooms = fetchedRooms.map(room => {
    const oldRoom = rooms.find(oldRoom => oldRoom.id === room.id)
    return {
      ...room,
      unreadMessages: oldRoom?.unreadMessages || []
    }
  })
  return [...rooms.filter(room => room.isDummyRoom), ...newRooms]
}

// Reconcile the freshly-fetched rooms (`items`) with what is already in the list
// (`stateRooms`) for a ROOMS_RECEIVED update.
//
// - newSearch: the fetch reset pagination (page 1 / filter or search change).
// - addToTop: the caller optimistically placed item(s) at the top of the list
//   (e.g. a just-invited patient, marked `isOptimistic`) — only those marked
//   rows survive the refresh, so we don't disturb other already-loaded pages.
export const mergeReceivedRooms = ({ stateRooms, items, newSearch, addToTop }) => {
  if (newSearch) {
    const refreshed = updateRoomsWithUnreadMessages(stateRooms, items)
    if (!addToTop) return refreshed
    // Keep only optimistically-added rooms (marked `isOptimistic` by
    // ADD_NEW_PATIENT_CHAT) that the lagging search index hasn't returned yet. The
    // marker self-clears: once the index returns the row it lands in `items`
    // (excluded here; the canonical copy comes from `refreshed`), and a plain
    // `newSearch` replace drops it. It's inert for rendering, so it may safely
    // outlive a refresh (e.g. the records-patient flow has no follow-up fetch).
    // We deliberately do NOT preserve other already-loaded rows: on a page-1
    // refresh, page 2+ rows are "missing" from `items`, and preserving them would
    // hoist them above page 1 and let infinite scroll re-append them
    // (roomsLoadedCount resets to items.length). Dummy rows are excluded here —
    // `refreshed`/updateRoomsWithUnreadMessages already retains them.
    const pendingRooms = stateRooms.filter(
      patientSM => patientSM.isOptimistic && !patientSM.isDummyRoom && !find(items, matchesProperty('id', patientSM.id))
    )
    return [...pendingRooms, ...refreshed]
  }
  return addToTop
    ? [...items, ...stateRooms.filter(patientSM => !find(items, matchesProperty('id', patientSM.id)))]
    : [...stateRooms, ...items]
}
